<template>
  <div class="booking-page">
    <section class="booking-hero">
      <div class="hero-copy">
        <p class="eyebrow">Free 30-minute consultation</p>
        <h1>Pick a direction.<br><em>Bring the confusion.</em></h1>
        <p>
          Send a quick booking request with your course, the topic you’re
          working on, and a few times that suit you. I’ll reply by email to
          confirm the consultation.
        </p>
        <div class="consult-details">
          <span><strong>30 min</strong> Completely free</span>
          <span><strong>Online</strong> Meet from anywhere</span>
          <span><strong>No pressure</strong> Just a useful conversation</span>
        </div>
      </div>

      <aside class="hero-note">
        <span class="note-pin" aria-hidden="true"></span>
        <p>What should I bring?</p>
        <ul>
          <li>Your course or topic</li>
          <li>An upcoming deadline</li>
          <li>The question that won’t click</li>
        </ul>
        <small>No perfect explanation needed. “I’m lost” is enough.</small>
      </aside>
    </section>

    <section class="request-section">
      <header class="request-heading">
        <div>
          <p class="section-kicker">Booking request</p>
          <h2>Tell me what works for you.</h2>
        </div>
        <p>
          This creates a ready-to-send email in your mail app. Nothing gets lost
          in an unreliable website form, and you stay in control of what you send.
        </p>
      </header>

      <div class="request-layout">
        <form class="booking-form" @submit.prevent="prepareBookingEmail">
          <div class="field-row">
            <div class="form-field">
              <label for="booking-name">Your name</label>
              <input
                id="booking-name"
                v-model.trim="form.name"
                type="text"
                autocomplete="name"
                placeholder="First and last name"
                required
              >
            </div>
            <div class="form-field">
              <label for="booking-email">Your email</label>
              <input
                id="booking-email"
                v-model.trim="form.email"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                required
              >
            </div>
          </div>

          <div class="form-field">
            <label for="booking-course">Course or topic</label>
            <input
              id="booking-course"
              v-model.trim="form.course"
              type="text"
              placeholder="For example: Financial Accounting, journal entries"
              required
            >
          </div>

          <div class="form-field">
            <label for="booking-times">A few times that work</label>
            <input
              id="booking-times"
              v-model.trim="form.times"
              type="text"
              placeholder="For example: Tue after 4pm ET, Wed 6–8pm ET"
              required
            >
            <small>Include your time zone so I can confirm the right time.</small>
          </div>

          <div class="form-field">
            <label for="booking-message">What would you like help with?</label>
            <textarea
              id="booking-message"
              v-model.trim="form.message"
              rows="6"
              placeholder="Share the concept, assignment, or exam you’re preparing for."
              required
            ></textarea>
          </div>

          <button type="submit" class="submit-button">
            Open my booking email <span aria-hidden="true">↗</span>
          </button>
          <p class="form-note">
            Your email app will open with the request filled in. Review it, then
            press send to request your consultation.
          </p>
        </form>

        <aside class="next-steps">
          <p class="aside-label">What happens next</p>
          <ol>
            <li>
              <span>01</span>
              <div>
                <strong>Your email opens</strong>
                <p>The details above are placed into a tidy booking request.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>Ali confirms a time</strong>
                <p>You’ll receive a reply with the agreed date and meeting details.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>You bring one question</strong>
                <p>We use the free call to identify the most useful first step.</p>
              </div>
            </li>
          </ol>

          <div class="direct-email">
            <p>Prefer to write your own message?</p>
            <a href="mailto:alifortutoring@gmail.com">
              alifortutoring@gmail.com
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </aside>
      </div>
    </section>

    <section class="expect-section">
      <article>
        <span>01</span>
        <h3>No broken handoff</h3>
        <p>Your request goes through the email account you already trust.</p>
      </article>
      <article>
        <span>02</span>
        <h3>Useful context first</h3>
        <p>Course, topic, deadline, and availability arrive in one clear note.</p>
      </article>
      <article>
        <span>03</span>
        <h3>A confirmed time</h3>
        <p>Ali replies directly so you know the consultation is actually booked.</p>
      </article>
    </section>
  </div>
</template>

<script setup>
import { reactive } from "vue";

const form = reactive({
  name: "",
  email: "",
  course: "",
  times: "",
  message: "",
});

const prepareBookingEmail = () => {
  const subject = encodeURIComponent(
    "Free consultation request — " + form.name
  );
  const body = encodeURIComponent(
    [
      "Hi Ali,",
      "",
      "I’d like to request a free 30-minute consultation.",
      "",
      "Name: " + form.name,
      "Reply email: " + form.email,
      "Course or topic: " + form.course,
      "Times that work (with time zone): " + form.times,
      "",
      "What I’d like help with:",
      form.message,
      "",
      "Thank you!",
    ].join("\n")
  );

  window.location.href =
    "mailto:alifortutoring@gmail.com?subject=" + subject + "&body=" + body;
};
</script>

<style lang="scss" scoped>
.booking-page {
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
}

.booking-hero {
  display: grid;
  grid-template-columns: 1fr 0.62fr;
  align-items: center;
  gap: clamp(3rem, 8vw, 8rem);
  min-height: 670px;
  padding: clamp(7rem, 12vw, 10rem) max(1.5rem, calc((100vw - 1180px) / 2));
  background:
    radial-gradient(circle at 15% 100%, rgba(49, 87, 246, 0.45), transparent 36%),
    var(--ink);
  color: white;
}

.eyebrow,
.section-kicker {
  margin: 0 0 1.3rem;
  color: var(--amber);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.19em;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: clamp(3.3rem, 6.6vw, 6.8rem);
  font-weight: 800;
  letter-spacing: -0.07em;
  line-height: 0.91;
}

h1 em {
  color: var(--amber);
  font-family: Georgia, "Times New Roman", serif;
  font-weight: 400;
}

.hero-copy > p:not(.eyebrow) {
  max-width: 650px;
  margin: 2.3rem 0;
  color: #cbd1df;
  font-size: 1.08rem;
  line-height: 1.75;
}

.consult-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.consult-details span {
  padding: 0.75rem 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: 999px;
  color: #d8deea;
  font-size: 0.78rem;
}

.consult-details strong {
  color: white;
}

.hero-note {
  position: relative;
  padding: clamp(2rem, 5vw, 3.4rem);
  border-radius: 18px;
  background: var(--amber);
  box-shadow: 16px 16px 0 var(--blue);
  color: var(--ink);
  transform: rotate(1.7deg);
}

.note-pin {
  position: absolute;
  top: 1rem;
  left: 50%;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: var(--blue);
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.25);
}

.hero-note > p {
  margin: 0 0 1.3rem;
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: -0.035em;
}

.hero-note ul {
  margin: 0 0 1.5rem;
  padding-left: 1.25rem;
  line-height: 1.9;
}

.hero-note small {
  display: block;
  padding-top: 1rem;
  border-top: 1px solid rgba(10, 16, 32, 0.18);
  line-height: 1.55;
}

.request-section {
  width: min(1180px, calc(100% - 3rem));
  margin: 0 auto;
  padding: clamp(6rem, 11vw, 10rem) 0;
}

.request-heading {
  display: grid;
  grid-template-columns: 1fr 0.72fr;
  align-items: end;
  gap: 3rem;
  margin-bottom: 3.5rem;
}

.request-heading .section-kicker {
  color: var(--blue);
}

.request-heading h2 {
  margin: 0;
  font-size: clamp(2.6rem, 5vw, 5rem);
  letter-spacing: -0.06em;
  line-height: 0.98;
}

.request-heading > p {
  margin: 0;
  color: #5a6272;
  line-height: 1.75;
}

.request-layout {
  display: grid;
  grid-template-columns: 1.18fr 0.82fr;
  overflow: hidden;
  border: 1px solid #dce1eb;
  border-radius: 28px;
  background: white;
  box-shadow: 0 30px 90px rgba(10, 16, 32, 0.1);
}

.booking-form {
  padding: clamp(1.5rem, 5vw, 4.5rem);
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-field {
  margin-bottom: 1.3rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.55rem;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.form-field input,
.form-field textarea {
  width: 100%;
  border: 1px solid #d5dae5;
  border-radius: 12px;
  background: var(--paper);
  color: var(--ink);
  outline: none;
  transition: border 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.form-field input {
  min-height: 54px;
  padding: 0 1rem;
}

.form-field textarea {
  min-height: 150px;
  resize: vertical;
  padding: 1rem;
  line-height: 1.6;
}

.form-field input:focus,
.form-field textarea:focus {
  border-color: var(--blue);
  background: white;
  box-shadow: 0 0 0 4px rgba(49, 87, 246, 0.11);
}

.form-field small {
  display: block;
  margin-top: 0.45rem;
  color: #737b8b;
  line-height: 1.5;
}

.submit-button {
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  margin-top: 0.5rem;
  padding: 1rem 1.25rem;
  border: 0;
  border-radius: 999px;
  background: var(--blue);
  color: white;
  cursor: pointer;
  font-weight: 800;
  transition: transform 180ms ease, background 180ms ease;
}

.submit-button:hover {
  background: var(--blue-dark);
  transform: translateY(-2px);
}

.form-note {
  max-width: 540px;
  margin: 1rem 0 0;
  color: #6a7282;
  font-size: 0.82rem;
  line-height: 1.6;
}

.next-steps {
  padding: clamp(1.5rem, 5vw, 4.5rem);
  background:
    linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    var(--ink);
  background-size: 46px 46px;
  color: white;
}

.aside-label {
  margin: 0 0 2.5rem;
  color: var(--amber);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.next-steps ol {
  margin: 0;
  padding: 0;
  list-style: none;
}

.next-steps li {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 1rem;
  padding: 1.4rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.13);
}

.next-steps li > span {
  color: #9eb1ff;
  font-size: 0.72rem;
  font-weight: 800;
}

.next-steps strong {
  font-size: 1.02rem;
}

.next-steps li p {
  margin: 0.5rem 0 0;
  color: #bfc7d6;
  font-size: 0.88rem;
  line-height: 1.6;
}

.direct-email {
  margin-top: 2.4rem;
  padding: 1.3rem;
  border-radius: 14px;
  background: var(--amber);
  color: var(--ink);
}

.direct-email p {
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
}

.direct-email a {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-weight: 800;
  word-break: break-word;
}

.expect-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: white;
}

.expect-section article {
  min-height: 300px;
  padding: clamp(2rem, 5vw, 4rem);
  border-top: 1px solid #dce1eb;
}

.expect-section article + article {
  border-left: 1px solid #dce1eb;
}

.expect-section span {
  color: var(--blue);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.expect-section h3 {
  margin: 5rem 0 1rem;
  font-size: 1.5rem;
  letter-spacing: -0.035em;
}

.expect-section p {
  margin: 0;
  color: #5b6271;
  line-height: 1.7;
}

@media (max-width: 850px) {
  .booking-hero,
  .request-heading,
  .request-layout {
    grid-template-columns: 1fr;
  }

  .booking-hero {
    min-height: 0;
  }

  .hero-note {
    max-width: 520px;
  }

  .expect-section {
    grid-template-columns: 1fr;
  }

  .expect-section article {
    min-height: 240px;
  }

  .expect-section article + article {
    border-top: 1px solid #dce1eb;
    border-left: 0;
  }

  .expect-section h3 {
    margin-top: 3rem;
  }
}

@media (max-width: 580px) {
  .request-section {
    width: min(100% - 1rem, 1180px);
  }

  .field-row {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: clamp(3.2rem, 14vw, 4.7rem);
  }
}
</style>
